import React, { useMemo } from "react";

import { DataFieldInputSelector } from "@/components/DataFieldInputSelector";
import { useGetDatesThatMatterFilterOptions } from "@/utils/react-query-hooks/lenses";
import { useGetQueryData } from "@/utils/react-query-hooks/general";
import { getTplsWithAnnosEnabled } from "@/components/analysisModules/AnnotationExplorer/useGetAnnotationExplorerData";

import { ConfigForm_SectionWrapper } from "../../../WidgetMetricCard/ConfigForm/ConfigForm_SectionWrapper";
import { WidgetFilters } from "../../../utils/WidgetFilters";
import { useWidgetFilterControls } from "../../../utils/useWidgetFilterControls";

import { DEFAULTS, TREND_WINDOW_OPTIONS, periodPickerOptions, pickerValue, resolvePeriod } from "../TempPriorities/settings";

/**
 * The Signal categories over time widget's config: the period the chart ends
 * on and how many periods it spans (the Priorities widget's Period section,
 * as is), and the widget's own filters over every annotation-enabled
 * template, at `<propsPath>.widgetFilters`.
 */
export const SignalCategoryTrendConfigForm = ({ props, updateProps, propsPath, widgetConfig, setConfig, underEditWidgetIdx }) => {
  const periodValue = props?.periodValue ?? DEFAULTS.periodValue;
  const trendWindow = props?.trendWindow ?? DEFAULTS.trendWindow;
  const allTpls = useGetQueryData("allTpls") || [];
  const annoTpls = useMemo(() => getTplsWithAnnosEnabled({ allTpls }), [allTpls]);
  const { filterHeaderProps, filterControlProps } = useWidgetFilterControls({ hasDimensions: annoTpls.length > 0 });

  const { data: dateFilterOptions } = useGetDatesThatMatterFilterOptions();
  const periodOptions = useMemo(() => periodPickerOptions(dateFilterOptions?.periods), [dateFilterOptions]);
  // The "Latest month" card names the month it currently resolves to.
  const latest = resolvePeriod(dateFilterOptions?.periods, DEFAULTS.periodValue);
  const periodPicked = pickerValue(periodOptions, periodValue, "Period");
  const periodCard =
    periodPicked && periodValue === DEFAULTS.periodValue && latest
      ? { ...periodPicked, display: `Latest month — ${latest.display}` }
      : periodPicked;

  return (
    <>
      <ConfigForm_SectionWrapper icon="CalendarBlank" title="Period">
        <DataFieldInputSelector
          id="periodValue"
          options={periodOptions}
          value={periodCard}
          onChange={(_id, option) =>
            updateProps((p) => ({ ...p, periodValue: option?.value ?? DEFAULTS.periodValue }))
          }
          placeholder="Period"
          placeholderIcon="CalendarBlank"
          trailingIcon="CaretRight"
        />
        <DataFieldInputSelector
          id="trendWindow"
          options={TREND_WINDOW_OPTIONS}
          value={pickerValue(TREND_WINDOW_OPTIONS, trendWindow, "Trend over")}
          onChange={(_id, option) =>
            updateProps((p) => ({ ...p, trendWindow: option?.value ?? DEFAULTS.trendWindow }))
          }
          placeholder="Trend over"
          placeholderIcon="CalendarBlank"
          trailingIcon="CaretRight"
          hideSearchHeader
        />
      </ConfigForm_SectionWrapper>

      <ConfigForm_SectionWrapper icon="Funnel" title="Filters" {...filterHeaderProps}>
        <WidgetFilters
          widgetConfig={widgetConfig}
          updateWidgetConfig={setConfig}
          widgetIndex={underEditWidgetIdx}
          selectedTpls={annoTpls}
          configPath={`${propsPath}.widgetFilters`}
          {...filterControlProps}
        />
      </ConfigForm_SectionWrapper>
    </>
  );
};
